import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { BottomNavigation } from "@/components/BottomNavigation";
import { PageTransition } from "@/components/PageTransition";
import { MenuDrawer } from "@/components/MenuDrawer";
import { PostCard } from "@/components/social/PostCard";
import { CreatePostDialog } from "@/components/social/CreatePostDialog";
import { usePosts } from "@/hooks/usePosts";
import { useAdminRole } from "@/hooks/useAdminRole";
import { RefreshCw, Sparkles, Plus, Trash2, Settings2 } from "lucide-react";
import TeclyPullRefresh from "@/components/TeclyPullRefresh";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const Bacheca = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null);
  const [showRoomManager, setShowRoomManager] = useState(false);
  const [newRoomName, setNewRoomName] = useState("");
  const [newRoomDesc, setNewRoomDesc] = useState("");
  
  const { isAdmin } = useAdminRole(userId);
  
  const {
    posts,
    loading,
    currentUserId,
    rooms,
    createPost,
    toggleLike,
    deletePost,
    fetchComments,
    addComment,
    deleteComment,
    refreshPosts,
    createRoom,
    deleteRoom,
  } = usePosts(activeRoomId);

  // Pull-to-refresh state
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const mainRef = useRef<HTMLDivElement>(null);
  const PULL_THRESHOLD = 80;

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (mainRef.current && mainRef.current.scrollTop <= 0) {
      touchStartY.current = e.touches[0].clientY;
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (isRefreshing) return;
    if (mainRef.current && mainRef.current.scrollTop <= 0) {
      const diff = e.touches[0].clientY - touchStartY.current;
      if (diff > 0) {
        setPullDistance(Math.min(diff * 0.5, 120));
      }
    }
  }, [isRefreshing]);

  const handleTouchEnd = useCallback(async () => {
    if (pullDistance >= PULL_THRESHOLD && !isRefreshing) {
      setIsRefreshing(true);
      await refreshPosts();
      setIsRefreshing(false);
    }
    setPullDistance(0);
  }, [pullDistance, isRefreshing, refreshPosts]);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate("/auth"); return; }
      setUserId(session.user.id);
      setIsAuthenticated(true);
    };
    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      if (!session) navigate("/auth");
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleCreateRoom = async () => {
    if (!newRoomName.trim()) return;
    await createRoom(newRoomName.trim(), newRoomDesc.trim());
    setNewRoomName("");
    setNewRoomDesc("");
  };

  if (!isAuthenticated) return null;

  return (
    <PageTransition>
      <div
        ref={mainRef}
        className="min-h-screen bg-background pb-32"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Pull-to-refresh indicator */}
        <TeclyPullRefresh pullDistance={pullDistance} isRefreshing={isRefreshing} />
        {/* Header */}
        <header className="sticky top-0 z-40 bg-background/90 backdrop-blur-lg border-b border-border/50">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
            <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Bacheca
            </h1>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <button
                  onClick={() => setShowRoomManager(true)}
                  className="p-2 rounded-full hover:bg-muted transition-all active:scale-90"
                >
                  <Settings2 className="w-5 h-5 text-muted-foreground" />
                </button>
              )}
              <button
                onClick={refreshPosts}
                className="p-2 rounded-full hover:bg-muted transition-all active:scale-90"
              >
                <RefreshCw className="w-5 h-5 text-muted-foreground" />
              </button>
              <CreatePostDialog
                onCreatePost={createPost}
                rooms={rooms}
                currentRoomId={activeRoomId}
                isAdmin={isAdmin}
              />
            </div>
          </div>

          {/* Room tabs */}
          {rooms.length > 0 && (
            <div className="container mx-auto px-4 pb-3">
              <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                <button
                  onClick={() => setActiveRoomId(null)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border transition-all ${
                    !activeRoomId
                      ? "bg-primary text-primary-foreground border-primary shadow-sm"
                      : "bg-muted/50 text-muted-foreground border-border hover:border-primary/40"
                  }`}
                >
                  Tutti
                </button>
                {rooms.map(room => (
                  <button
                    key={room.id}
                    onClick={() => setActiveRoomId(room.id)}
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border transition-all ${
                      activeRoomId === room.id
                        ? "bg-primary text-primary-foreground border-primary shadow-sm"
                        : "bg-muted/50 text-muted-foreground border-border hover:border-primary/40"
                    }`}
                  >
                    {room.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </header>

        {/* Feed */}
        <main className="container mx-auto px-4 py-4 max-w-lg space-y-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              <p className="text-sm text-muted-foreground">Caricamento...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Nessun post ancora</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {activeRoomId ? "Nessun post in questa stanza" : "Sii il primo a condividere qualcosa!"}
                </p>
              </div>
            </div>
          ) : (
            posts.map(post => (
              <PostCard
                key={post.id}
                post={post}
                currentUserId={currentUserId}
                onToggleLike={toggleLike}
                onDelete={deletePost}
                onFetchComments={fetchComments}
                onAddComment={addComment}
                onDeleteComment={deleteComment}
                isAdmin={isAdmin}
              />
            ))
          )}
        </main>

        {/* Room Manager (admin) */}
        <Dialog open={showRoomManager} onOpenChange={setShowRoomManager}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Gestione Stanze</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Create room */}
              <div className="space-y-2">
                <Input
                  value={newRoomName}
                  onChange={(e) => setNewRoomName(e.target.value)}
                  placeholder="Nome stanza"
                  maxLength={50}
                />
                <Textarea
                  value={newRoomDesc}
                  onChange={(e) => setNewRoomDesc(e.target.value)}
                  placeholder="Descrizione (opzionale)"
                  className="resize-none"
                  rows={2}
                  maxLength={200}
                />
                <Button onClick={handleCreateRoom} disabled={!newRoomName.trim()} className="w-full" size="sm">
                  <Plus className="w-4 h-4 mr-2" /> Crea Stanza
                </Button>
              </div>

              {/* Room list */}
              {rooms.length > 0 && (
                <div className="border-t border-border pt-4 space-y-2">
                  <p className="text-sm font-medium text-foreground">Stanze esistenti</p>
                  {rooms.map(room => (
                    <div key={room.id} className="flex items-center justify-between p-3 rounded-xl border border-border bg-muted/30">
                      <div>
                        <p className="text-sm font-medium text-foreground">{room.name}</p>
                        {room.description && <p className="text-xs text-muted-foreground">{room.description}</p>}
                      </div>
                      <button
                        onClick={() => deleteRoom(room.id)}
                        className="p-2 rounded-full hover:bg-destructive/10 transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <BottomNavigation
          activeTab="bacheca"
          onMenuClick={() => setIsMenuOpen(true)}
        />
        <MenuDrawer open={isMenuOpen} onOpenChange={setIsMenuOpen} />
      </div>
    </PageTransition>
  );
};

export default Bacheca;
